<mat-horizontal-stepper #stepper [linear]="isLinear">
  <mat-step [stepControl]="firstFormGroup">
    <form [formGroup]="firstFormGroup">
      <ng-template matStepLabel>
        <div>
          <span active>Update Cascade</span>
        </div>
      </ng-template>

      <div class="mt-3 flex-gap-15px flex-row">
        <div class="flex-45">
          <mat-card appearance="outlined">
            <mat-card-header>
              <mat-card-title>Cascade Path</mat-card-title>
              <mat-card-subtitle class="sub-title">
                <h6>Select menu path below to apply item updates</h6>
                <mat-icon class="icon-help"
                  fontIcon="help_outline"
                  matTooltip="Find selected parent from anywhere on selected submenus. Select submenus before the ancestors.">
                </mat-icon>
              </mat-card-subtitle>
            </mat-card-header>
            <mat-card-content class="ml-3 left-card">
              <div>
                <div class="filter-grp-wrapper">
                  <mat-form-field class="filter-grp">
                    <mat-label>Filter</mat-label>
                    <input matInput [(ngModel)]="filterValue" [ngModelOptions]="{standalone: true}" (input)="onFilterChange($event)"/>
                    <div class="filter-size" [matBadge]="filterSize" matBadgePosition="after" matBadgeColor="warn"></div>
                    <button mat-icon-button class="close-btn" (click)="clearFilter()">
                      <mat-icon class="close-icon">close</mat-icon>
                    </button>
                  </mat-form-field>
                </div>
                <mat-list class="path-list">
                  @for (submenu of submenus; track submenu) {
                    <mat-list-item style="height: 40px;" class="flex-column">
                      <mat-checkbox
                                            [checked]="currentSubmenu === submenu.id ||
                                        selectedSubmenus.includes(submenu.id)"
                        [disabled]="currentSubmenu === submenu.id"
                        (change)="updateAllSelected($event.checked, submenu.id)">
                        {{submenu.name}}&nbsp;-&nbsp;{{submenu.id}}
                      </mat-checkbox>
                    </mat-list-item>
                  }
                </mat-list>
              </div>
              <div>
                <h6>Select parent ancestors to apply</h6>
                <mat-chip-listbox aria-label="Parents Path" class="mat-mdc-chip-set-stacked">
                  @for (chip of bottomChips$ | async; track chip) {
                    <div class="flex-row">
                      <mat-icon class="mt-1">expand_more</mat-icon>
                      <mat-chip-option
                        [selected]="chip.selected"
                        [matTooltip]="chip.tag"
                        matTooltipPosition="right"
                        [ngClass]="chip.selected ? 'selected':  'deselected'"
                        (click)="toggleSelectBottomChip(chip)"
                      >{{ chip.name }}</mat-chip-option>
                    </div>
                  }
                </mat-chip-listbox>
                <div class="status">
                  @if (spinner$ | async) {
                    <mat-progress-spinner
                      [color]="color"
                      [diameter]="diameter"
                      [mode]="mode"
                      >
                    </mat-progress-spinner>
                  }
                  @if (!(spinner$ | async)) {
                    <div class="flex-row">
                      <div><strong>Found {{ matchCount$ | async }} matches for path</strong></div>
                      <div><mat-icon class="info" (click)="togglePath()">info</mat-icon></div>
                    </div>
                  }
                </div>
                @if (showPath$ | async) {
                  <div><code>{{ path$ | async | json }}</code></div>
                }
              </div>
            </mat-card-content>
          </mat-card>
        </div>

        <div class="flex-55">
          <mat-card appearance="outlined">
            <mat-card-header>
              <mat-card-title>Item Updates</mat-card-title>
              <mat-card-subtitle>
                The following updates will be made to {{ itemDetail?.tag }}. To override existing values at lower hierarchies, check the box to override.
              </mat-card-subtitle>
            </mat-card-header>
            @if ((newUpdateFlow$ | async)) {
              <mat-card-content class="right-card ml-3">
                <div class="flex-column">
                  @if (!(isLoading | async) &&
                    (dataSource | async).length > 0) {
                    <div class="right-card">
                      <table mat-table [dataSource]="dataSource | async">
                        <ng-container matColumnDef="attribute">
                          <th mat-header-cell *matHeaderCellDef>Attribute</th>
                          <td mat-cell *matCellDef="let row">{{ row.attribute }}</td>
                        </ng-container>
                        <ng-container matColumnDef="locations">
                          <th mat-header-cell *matHeaderCellDef>Locations with Overrides</th>
                          <td mat-cell *matCellDef="let row">{{ row.locations }}</td>
                        </ng-container>
                        <ng-container matColumnDef="currentValue">
                          <th mat-header-cell *matHeaderCellDef>Current Value</th>
                          <td mat-cell *matCellDef="let row">{{ row.currentValue }}</td>
                        </ng-container>
                        <ng-container matColumnDef="newValue">
                          <th mat-header-cell *matHeaderCellDef>New Value</th>
                          <td mat-cell *matCellDef="let row">{{ row.newValue }}</td>
                        </ng-container>
                        @if (locationSet !== 'Location') {
                          <ng-container matColumnDef="forceOverrides">
                            <th mat-header-cell *matHeaderCellDef>Force Overrides? </th>
                            <td mat-cell *matCellDef="let row">
                              <mat-checkbox
                                [checked]="row.forceOverrides"
                                (change)="forceOverrideForAttribute(row)"
                              >Force Override? </mat-checkbox>
                            </td>
                          </ng-container>
                        }
                        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                        <tr mat-row *matRowDef="let r; columns: displayedColumns;"></tr>
                      </table>
                    </div>
                  }
                  @if (isLoading | async) {
                    <div class="loading-container-center">
                      <mat-spinner diameter="60"></mat-spinner>
                      <p class="loading-text">Loading item changes & overrides...</p>
                    </div>
                  }
                  @if (!(isLoading | async) && (dataSource | async)?.length === 0) {
                    <div style="text-align: center">
                      No overrides found for {{ this.itemDetail?.name }}
                    </div>
                  }
                </div>
              </mat-card-content>
            }
            @if (!(newUpdateFlow$ | async)) {
              <mat-card-content class="ml-3">
                <div class="flex-column">
                  @for (item of this.data.updates | keyvalue; track item) {
                    <div>
                      <div class="flex-row">
                        <div class="flex-40">
                          <label>{{ item.key | camelCaseToSentenceCase }}</label>
                        </div>
                        @if (item.value !== null) {
                          <div class="flex-offset-3 flex-60">
                            {{ item.value }}
                          </div>
                        } @else {
                          {{ removedMessage }}
                        }
                      </div>
                    </div>
                  }
                </div>
              </mat-card-content>
            }
          </mat-card>
          @if (locationSet !== 'Location') {
            <div class="force-change-text">
              <mat-card-subtitle>Forcing overrides will apply this change to all menus and any pre-existing override data will be lost.</mat-card-subtitle>
            </div>
          }
          @if (locationSet !== 'Location' && !(newUpdateFlow$ | async)) {
            <div class="force-change flex-column">
              <mat-checkbox
                [checked]="forceOverrides"
                #cascadeCheckbox
                >Force Change & Erase Overrides
              </mat-checkbox>
              <mat-card-subtitle>This will apply this change to all menus and any pre-existing override data will be lost.</mat-card-subtitle>
            </div>
          }
        </div>
      </div>

      <div class="buttons flex-center">
        <button
          [attr.data-testid]="'add_item_cancel_button'"
          class="button-color"
          mat-raised-button
          matTooltip="Cancel"
          (click)="onNoClick()"
        >Cancel</button>
        <button
          [attr.data-testid]="'add_item_continue_button'"
          class="button-color"
          mat-raised-button
          matStepperNext
          matTooltip="Continue"
          [disabled]="isLoading | async"
        >Continue</button>
      </div>
    </form>
  </mat-step>

  <mat-step>
    <div>
      <ng-template matStepLabel>Confirm Update</ng-template>
      <div>
        <div class="mb-2">
          Are you sure you want to update cascade to all <strong>{{ matchCount$ | async }}</strong>
          matches of item path
        </div>
        @for (selectedParent of selectedBottomChips$ | async; track selectedParent) {
          <div>
            <div class="flex-row">
              <mat-icon class="icon">expand_more</mat-icon>
              <h5 class="item-bold">{{ selectedParent.name }} ({{ selectedParent.tag }})</h5>
            </div>
          </div>
        }
        @if (subMenu) {
          <div>
            <span>that are only under sub-menu</span>
            <h5 class="item-bold">{{ subMenu?.name }} ({{ subMenu?.tag }}) ?</h5>
          </div>
        } @else {
          @if (featureScope === 'Legacy') {
            <div>across this entire menu? </div>
          } @else {
            <div>across the selected submenus?</div>
          }
        }
      </div>
      <div class="buttons flex-center-center">
        <button
          [attr.data-testid]="'confirm_cancel_button'"
          class="button-color"
          mat-raised-button
          matTooltip="Cancel"
          (click)="onNoClick()"
        >Cancel</button>
        <button
          [attr.data-testid]="'confirm_back_button'"
          class="button-color"
          mat-raised-button
          matStepperPrevious
          matTooltip="Back"
        >Back</button>
        <button
          [attr.data-testid]="'confirm_yes_button'"
          class="button-color"
          mat-raised-button
          matTooltip="Yes"
          [disabled]="isLoading | async"
          (click)="onSave()"
        >Yes
        </button>
      </div>
    </div>
  </mat-step>

</mat-horizontal-stepper>
